/* eslint-disable max-len */
// Common utility functions shared across different helper modules

import { SUPPORTED_IMAGE_FORMATS, SUPPORTED_DOCUMENT_FORMATS } from '../types';

/**
 * Helper function to extract file extension from URL
 */
export const extractFileTypeFromUrl = (url: string): string | null =>
{
	try
	{
		// Check if it's a data URL (starts with data:)
		if (url.startsWith('data:'))
		{
			// For data URLs, extract the file type from the MIME type
			// Format: data:image/png;base64,...
			const dataUrlRegex = /^data:image\/([a-z0-9.+-]+);base64,/i;
			const matches = url.match(dataUrlRegex);

			if (matches && matches.length > 1)
			{
				const fileType = matches[1].toLowerCase();

				return fileType;
			}

			// If the regex didn't match but it's still an image data URL
			if (url.startsWith('data:image/'))
			{
				return 'png'; // Default to png if we can't extract the specific type
			}

			return null;
		}

		// Enhanced Google image URL detection - covers more Google image URL patterns
		if (url.includes('googleusercontent.com') ||
			url.includes('encrypted-tbn') ||
			url.includes('google.com') && (
				url.includes('=pa-nu-') ||
				url.includes('=e365-') ||
				url.includes('=w') ||
				url.includes('&q=') ||
				url.includes('/images')
			)
		)
		{
			// Check if we can find WEBP format indicator in the URL
			if (url.includes('WEBP') || url.includes('webp'))
			{
				return 'webp';
			}

			// Default to jpg for Google images - most common format
			return 'jpg';
		}

		// Get the pathname from URL
		const pathname = new URL(url).pathname;

		// First try to extract extension from the pathname
		const extension = pathname.split('.').pop()
			?.toLowerCase();

		// If we got a valid extension, return it
		if (extension && extension.length <= 5 && extension !== pathname)
		{
			return extension;
		}

		// Check for URL parameters indicating image type
		const urlObj = new URL(url);
		const format = urlObj.searchParams.get('format') || urlObj.searchParams.get('type') || urlObj.searchParams.get('ext');

		if (format && [ 'jpg', 'jpeg', 'png', 'gif', 'webp' ].includes(format.toLowerCase()))
		{
			return format.toLowerCase();
		}

		// Try to guess from URL patterns
		if (url.includes('.png') || url.includes('=png'))
		{
			return 'png';
		}
		if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('=jpg') || url.includes('=jpeg'))
		{
			return 'jpg';
		}
		if (url.includes('.gif') || url.includes('=gif'))
		{
			return 'gif';
		}
		if (url.includes('.webp') || url.includes('=webp'))
		{
			return 'webp';
		}

		return null;
	}
	catch (e)
	{
		return null;
	}
};

/**
 * Helper function to validate if URL has a supported file extension
 */
export const validateFileTypeFromUrl = (url: string, supportedFormats: string[]): boolean =>
{
	// Special case for data URLs
	if (url.startsWith('data:'))
	{
		const fileType = extractFileTypeFromUrl(url);

		// If we can extract a specific file type, check if it's supported
		if (fileType && supportedFormats.includes(fileType))
		{
			return true;
		}

		// If we can't extract a specific type but it's clearly an image, accept it
		// This is a fallback for generic image data URLs
		if (url.startsWith('data:image/'))
		{
			return true;
		}

		return false;
	}

	// Enhanced handling for Google image URLs with complex paths and parameters
	if (url.includes('googleusercontent.com') ||
		url.includes('encrypted-tbn') ||
		url.includes('google.com/images') ||
		url.includes('google.com') && (
			url.includes('=pa-nu-') ||
			url.includes('=e365-') ||
			url.includes('&q=') ||
			url.includes('=w')
		) ||
		url.includes('lh3.googleusercontent.com'))
	{
		return true; // Trust all Google image URLs
	}

	const extension = extractFileTypeFromUrl(url);

	return extension ? supportedFormats.includes(extension) : false;
};

/**
 * Generate a simple ID for naming purposes
 */
export const generateRandomId = (): string =>
{
	return Math.random().toString(36)
		.substr(2, 9);
};

/**
 * Check if URL has multiple URLs
 */
export const hasMultipleUrls = (url: string): boolean =>
{
	const hasNewlines = url.includes('\n');
	const hasMultipleHttps = (url.match(/https?:\/\//gi) || []).length > 1;
	const hasSpacedUrls = url.match(/\s+https?:\/\//i) !== null;
	const hasSeparators = url.match(/[,;|]https?:\/\//i) !== null;

	return hasNewlines || hasMultipleHttps || hasSpacedUrls || hasSeparators;
};

/**
 * Check if URL is valid HTTP/HTTPS format
 */
export const isValidHttpUrl = (url: string): boolean =>
{
	return url.match(/^https?:\/\//i) !== null;
};